/* eslint-disable no-param-reassign */
/* eslint-disable import/no-named-default */
import React, { useReducer, useEffect, useRef } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import clsx from 'clsx';
import {
    makeStyles, Button, Grid,
    FormControl, FormControlLabel,
    Radio, RadioGroup, Divider,
    Dialog, DialogContent,
    DialogActions, Select,
    MenuItem, Checkbox, ListItemText,
} from '@material-ui/core';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LotQuery from 'services/graphQL/query/LotQuery';
import LotMutation from 'services/graphQL/mutate/LotMutation';
import CatalogEnum from 'utils/enum/CatalogEnum';
import CatalogQuery from 'services/graphQL/query/CatalogQuery';
import AIQuery from 'services/graphQL/query/ai/AIQuery';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import UserQuery from 'services/graphQL/query/UserQuery';
import { InventoryCategory } from 'utils/enum/InventoryEnum';
import { ALL_LOTS, FetchPolicy } from 'utils/enum/Core';
import { Col, Form } from 'react-bootstrap';
import ArrayUtils from 'lib/ArrayUtils';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import CompanySettingCategory, { CompanySettingkey } from 'utils/enum/CompanySettingEnum';
import LotsCategory, { LotDefaultskey } from 'utils/enum/LotsCategory';
import If from 'components/widgets/conditional/If';
import ButtonStyles from 'styles/theme/Button';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import { default as SelectWidget } from 'components/widgets/Select';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import CatalogManager from 'components/modules/settings/inventory/CatalogManager';
import InputNumber from 'components/widgets/InputNumber';
import VirtualTable from 'components/widgets/VirtualTable';
import RoleBuilder from 'components/widgets/settings/RoleBuilder';

// Icons
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';
import ImportExportOutlinedIcon from '@material-ui/icons/ImportExportOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        paddingBottom: '10px',
        border: `solid 1px ${theme.palette.border.mercury}`,
        '& > div > button': {
            marginRight: '10px',
        },
    },
    boxContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    paddingLeft25: {
        paddingLeft: '25px',
        paddingRight: '25px',
    },
    otherActions: {
        '& > button': {
            marginTop: '2px',
        },
        '& > button:nth-child(1), & > button:nth-child(2)': {
            marginRight: '10px',
        },
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            '& > button:nth-child(1), & > button:nth-child(2)': {
                marginBottom: '5px',
                width: '100%',
            },
            '& > button:nth-child(3)': {
                width: '100%',
            },
        },
    },
    labels: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
    },
    subLabel: {
        fontSize: '12px',
    },
    tableContainer: {
        marginTop: '15px',
        paddingLeft: '15px',
        paddingRight: '15px',
        height: '380px',
        overflow: 'hidden',
        '& > div': {
            overflow: 'hidden',
        },
    },
    tableHeader: {
        textAlign: 'left',
        color: theme.palette.text.waterloo,
        borderRight: `1px solid ${theme.palette.border.ghost}`,
        height: '100%',
        alignItems: 'center',
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& > button:nth-child(1)': {
            marginRight: '2px',
        },
        '& > button': {
            minWidth: '32px',
            '& .MuiButton-startIcon': {
                marginRight: '0px',
            },
        },
    },
    AppBar: {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.background.sanMarino,
        '& h4': {
            color: theme.palette.text.white,
        },
    },
    content: {
        padding: '20px 10px',
    },
    select: {
        width: '100%',
        border: `1px solid ${theme.palette.border.ghost}`,
        borderRadius: '0.25rem',
        height: '38px',
        '& > div': {
            paddingLeft: '10px !important',
            paddingRight: '10px !important',
        },
    },
    dialogActions: {
        '& > .form-group': {
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: '40px',
        },
    },
    dialogLabels: {
        fontSize: '14px',
    },
    input: {
        fontSize: '14px',
        resize: 'none',
    },
    '@global': {
        '.css-26l3qy-menu div': {
            fontSize: '14px',
            lineHeight: '1.4',
        },
    },
    fieldContainer: {
        '& > .select-bootstrap': {
            [theme.breakpoints.down('sm')]: {
                width: '100%',
            },
        },
        '& > .max-cost': {
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
            '& > input': {
                marginRight: '10px',
                [theme.breakpoints.down('sm')]: {
                    width: '100%',
                    marginBottom: '10px',
                    marginRight: 0,
                },
            },
        },
    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    flags: {
        marginLeft: '15px',
        marginBottom: '5px',
        '& > label': {
            fontSize: '13px',
            width: '140px',
        },
    },
    aiSection: {
        paddingBottom: '15px',
        '& > div:nth-child(1)': {
            marginTop: '20px',
            marginLeft: '5px',
            marginBottom: '10px',
            fontSize: '14px',
        },
        '& > div:nth-child(2)': {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '15px',
            flexDirection: 'row',
            paddingTop: '5px',
            '& > span': {
                padding: 0,
            },
            '& > div': {
                display: 'flex',
                marginRight: '10px',
                '& > div': {
                    width: '250px',
                    marginLeft: '10px',
                    '& > div': {
                        minHeight: '30px',
                    },
                },
            },
            '& > button': {
                padding: 0,
                height: '36px',
                minWidth: '80px',
            },
        },
    },
    assigneeParent: {
        marginTop: '20px',
        borderTop: `1px solid ${theme.palette.border.ghost}`,
        paddingTop: '10px',
        '& > div:nth-child(1)': {
            fontSize: '14px',
            fontWeight: 'bold',
            marginLeft: '15px',
        },
    },
}));

const MenuProps = {
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
    },
    PaperProps: {
        style: {
            maxHeight: 200,
            width: 250,
        },
    },
    getContentAnchorEl: null,
};

const INIT_STATE = {
    currentFilter: 0,
    categories: [],
    jobTypes: [],
    isAddFormOpen: false,
    selectedItem: {},
    isRemoveWarningOpen: false,
    isRestoringItem: false,
    isCatalogManagerFormOpen: false,
    isJobTypesCatalog: false,
    inspectors: [],
    defaultReconCost: 0,
    users: [],
    aiEnabled: false,
    aiAssistant: null,
    assistants: [],
};

const ACTION_TYPES = {
    SET_CURRENT_FILTER: 'setCurrentFilter',
    SET_CATEGORIES: 'setCategories',
    SET_JOB_TYPES: 'setJobTypes',
    TOGGLE_ADD_FORM: 'toggleAddForm',
    CHANGE_FIELD_IN_SELECTED: 'changeFieldInSelected',
    ADD_ITEM: 'addItem',
    ADD_CATEGORY: 'addCategory',
    MODIFY_ITEM: 'modifyItem',
    TOGGLE_DISPLAY_WARNING: 'toggleDisplayWarning',
    SET_SELECTED_ITEM: 'setSelectedItem',
    TOGGLE_CATALOG_MANAGER_FORM: 'toggleCatalogManagerForm',
    SET_INSPECTORS: 'setInspectors',
    SET_DEFAULT_INSPECTOR: 'setDefaultInspector',
    SET_DEFAULT_RECON_COST: 'setDefaultReconCosto',
    SET_USERS: 'setUsers',
    SET_ASSISTANTS: 'setAssistants',
    CHANGE_AI_DATA: 'changeAIData',
};

const reducer = (state, action = {}) => {
    let clone = null;
    let index = -1;

    switch (action.type) {
    case ACTION_TYPES.SET_CURRENT_FILTER:
        return {
            ...state,
            currentFilter: action.value,
        };
    case ACTION_TYPES.SET_CATEGORIES:
        const cats = cloneDeep(action.value);

        return {
            ...state,
            categories: cats.map((c) => {
                const { items } = c;
                items.forEach((item) => {
                    item.jobTypes = item.jobTypeIds?.split(',')?.map((id) => Number(id)) || [];
                    delete item.jobTypeIds;
                });

                return c;
            }),
        };
    case ACTION_TYPES.SET_JOB_TYPES:
        return {
            ...state,
            jobTypes: action.value,
        };
    case ACTION_TYPES.SET_SELECTED_ITEM:
        return {
            ...state,
            isRestoringItem: true,
            selectedItem: action.value,
        };
    case ACTION_TYPES.TOGGLE_ADD_FORM:
        const item = action.value;

        if (item) {
            return {
                ...state,
                selectedItem: item,
                isAddFormOpen: !state.isAddFormOpen,
            };
        }

        return {
            ...state,
            selectedItem: !state.isAddFormOpen ? {
                repairPriority: 8,
                dontCreateServiceJob: false,
                diagnoseByTechFirst: false,
            } : state.selectedItem,
            isAddFormOpen: !state.isAddFormOpen,
        };
    case ACTION_TYPES.TOGGLE_DISPLAY_WARNING:
        return {
            ...state,
            selectedItem: action.value,
            isRemoveWarningOpen: !state.isRemoveWarningOpen,
        };
    case ACTION_TYPES.TOGGLE_CATALOG_MANAGER_FORM:
        return {
            ...state,
            isCatalogManagerFormOpen: !state.isCatalogManagerFormOpen,
            isJobTypesCatalog: action.value,
        };
    case ACTION_TYPES.CHANGE_FIELD_IN_SELECTED:
        const { name, value } = action.value;
        clone = cloneDeep(state.selectedItem);

        if (name === 'category') {
            clone[name] = {
                ...(clone[name] || {}),
                reconInspectionCategoryId: value,
            };
        } else {
            clone[name] = value;
        }

        return {
            ...state,
            selectedItem: clone,
        };
    case ACTION_TYPES.ADD_ITEM:
        const reconInspectionItemId = action.value;
        const {
            category: { reconInspectionCategoryId },
            name: itemName,
            repairPriority,
            dontCreateServiceJob,
            diagnoseByTechFirst,
            employeeType,
        } = state.selectedItem;
        clone = cloneDeep(state.categories);

        index = clone.findIndex((cat) => cat.reconInspectionCategoryId === reconInspectionCategoryId);
        if (index >= 0) {
            const items = [
                ...(clone[index]?.items || []),
                {
                    reconInspectionItemId,
                    name: itemName,
                    repairPriority,
                    dontCreateServiceJob,
                    diagnoseByTechFirst,
                    employeeType,
                    active: true,
                },
            ];

            items.sort((a, b) => a.name.localeCompare(b.name));
            clone[index] = {
                ...clone[index],
                items,
            };
        }

        return {
            ...state,
            categories: clone,
            selectedItem: {},
        };
    case ACTION_TYPES.ADD_CATEGORY:
        clone = cloneDeep(state.categories);
        clone.push({
            reconInspectionCategoryId: action.value.id,
            name: action.value.name,
            order: action.value.order,
            active: true,
            items: [],
        });

        return {
            ...state,
            categories: clone,
            selectedItem: {
                ...state.selectedItem,
                category: { reconInspectionCategoryId: action.value.id },
            },
        };
    case ACTION_TYPES.MODIFY_ITEM:
        const { category } = state.selectedItem;
        clone = cloneDeep(state.categories);

        index = clone.findIndex((cat) => cat?.reconInspectionCategoryId === category?.reconInspectionCategoryId);
        if (index >= 0) {
            const items = [...(clone[index]?.items || [])];
            const itemIndex = items.findIndex((it) => it.reconInspectionItemId === state.selectedItem.reconInspectionItemId);
            const modifiedItem = {
                reconInspectionItemId: state.selectedItem.reconInspectionItemId,
                name: state.selectedItem.name,
                jobTypes: state.selectedItem.jobTypes,
                repairPriority: state.selectedItem.repairPriority,
                dontCreateServiceJob: state.selectedItem.dontCreateServiceJob,
                diagnoseByTechFirst: state.selectedItem.diagnoseByTechFirst,
                employeeType: state.selectedItem.employeeType,
                active: state.selectedItem.active,
            };

            const removingOrRestoring = (state.isRestoringItem || state.isRemoveWarningOpen);
            if (itemIndex >= 0 && !removingOrRestoring) {
                items[itemIndex] = modifiedItem;
            } else {
                if (!removingOrRestoring) items.push(modifiedItem);

                const oldCategory = action?.value?.category?.reconInspectionCategoryId || category.reconInspectionCategoryId;
                const oldCategoryIndex = clone.findIndex((cat) => cat.reconInspectionCategoryId === oldCategory);

                if (oldCategoryIndex >= 0) {
                    const oldCategoryItems = [...(clone[oldCategoryIndex]?.items || [])].filter(
                        (el) => el.reconInspectionItemId !== state.selectedItem.reconInspectionItemId,
                    );

                    clone[oldCategoryIndex] = {
                        ...clone[oldCategoryIndex],
                        items: oldCategoryItems,
                    };
                }
            }

            if (!removingOrRestoring) {
                items.sort((a, b) => a.name.localeCompare(b.name));
                clone[index] = {
                    ...clone[index],
                    items,
                };
            }
        }

        return {
            ...state,
            categories: clone,
            selectedItem: {},
            isRemoveWarningOpen: false,
            isRestoringItem: false,
        };
    case ACTION_TYPES.SET_INSPECTORS:
        return {
            ...state,
            inspectors: action.value,
        };
    case ACTION_TYPES.SET_DEFAULT_INSPECTOR:
        const userID = action.value;
        const inspectors = state.inspectors.map((inspector) => ({
            ...inspector,
            default: inspector.userID === userID,
        }));

        return {
            ...state,
            inspectors,
        };
    case ACTION_TYPES.SET_DEFAULT_RECON_COST:
        return {
            ...state,
            defaultReconCost: action.value,
        };
    case ACTION_TYPES.SET_USERS:
        return {
            ...state,
            users: action.value,
        };
    case ACTION_TYPES.SET_ASSISTANTS:
        return {
            ...state,
            assistants: action.value,
        };
    case ACTION_TYPES.CHANGE_AI_DATA:
        if ('aiEnabled' in action.value) {
            const { aiEnabled, aiAssistant } = action.value;
            return {
                ...state,
                aiEnabled,
                aiAssistant,
            };
        }

        const { name: fieldName, value: fieldValue } = action.value;
        return {
            ...state,
            [fieldName]: fieldValue,
        };
    default:
        return state;
    }
};

const ReconSettings = ({ canWrite }) => {
    const keyStore = new KeyStore();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const options = useRef(null);
    const classes = { ...useStyles(), ...buttonStyles() };
    const [state, dispatch] = useReducer(reducer, INIT_STATE);
    const {
        currentFilter,
        categories,
        jobTypes,
        isAddFormOpen,
        selectedItem,
        isRemoveWarningOpen,
        isCatalogManagerFormOpen,
        isJobTypesCatalog,
        inspectors,
        defaultReconCost,
        users,
        aiEnabled,
        aiAssistant,
        assistants,
    } = state;

    const getItems = (cats) => {
        let output = [];
        cats.forEach((category) => {
            output = [...output, ...([...category.items].map(
                (item) => ({
                    ...item,
                    category: {
                        reconInspectionCategoryId: category.reconInspectionCategoryId,
                        name: category.name,
                    },
                }),
            ))];
        });

        return output;
    };

    const {
        AI_ENABLED_FOR_RECON_APPROVALS,
        AI_ASSISTANT_FOR_RECON_APPROVALS,
    } = LotDefaultskey;

    const {
        data: aiSettings,
        loading: aiSettingsLoading,
        error: aiSettingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.AI,
            lotName: ALL_LOTS,
            key: [
                AI_ENABLED_FOR_RECON_APPROVALS,
                AI_ASSISTANT_FOR_RECON_APPROVALS,
            ],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: assistantsData,
        loading: loadingSssistants,
        error: errorLoadingAssistants,
    } = useQuery(AIQuery.PULL_ASSISTANTS, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: settingsData,
        loading: settingsLoading,
        error: settingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.INVENTORY,
            lotName: ALL_LOTS,
            key: [InventoryCategory.INV_DEFAULT_MAX_RECON_COST],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: reconInspectorsData,
        loading: reconInspectorsLoading,
        error: reconInspectorsError,
    } = useQuery(InventoryQuery.GET_RECON_INSPECTORS, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: usersData,
        loading: usersDataLoading,
        error: usersDataError,
    } = useQuery(UserQuery.GET_USERS_BY_COMPANY, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [getCategories, { loading: loadingCategories, refetch: refetchCategories }] = useLazyQuery(InventoryQuery.GET_INSPECTION_CATEGORIES, {
        onCompleted: (response) => {
            if (response) {
                const data = response.getInspectionCategories;
                dispatch({
                    type: ACTION_TYPES.SET_CATEGORIES,
                    value: data,
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const [getJobTypes, { loading: loadingJobTypes, refetch: refetchJobTypes }] = useLazyQuery(CatalogQuery.ENUM_VALUES, {
        onCompleted: (response) => {
            if (response) {
                const data = response.getEnumValues;
                dispatch({
                    type: ACTION_TYPES.SET_JOB_TYPES,
                    value: data,
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const [editJobTypes] = useMutation(CatalogQuery.SAVE_ENUM, {
        onCompleted: (response) => {
            if (!response) return;

            ModalUtils.successMessage(null, 'Job Types updated successfully');
            // eslint-disable-next-line no-use-before-define
            toggleCatalogManager(false);
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [saveDefaultInspector, { loading: savingDefaultInspector }] = useMutation(InventoryMutation.SAVE_RECON_DEFAULT_INSPECTOR, {
        onCompleted: (response) => {
            if (response) {
                dispatch({
                    type: ACTION_TYPES.SET_DEFAULT_INSPECTOR,
                    value: options.current,
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [addItem, { loading: addingItem }] = useMutation(InventoryMutation.ADD_INSPECTION_ITEM, {
        onCompleted: (response) => {
            if (response) {
                const id = response.addInspectionItem;
                dispatch({
                    type: ACTION_TYPES.ADD_ITEM,
                    value: id,
                });

                ModalUtils.successMessage(null, 'Item added successfully');
                // eslint-disable-next-line no-use-before-define
                toggleAddForm();
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [modifyItem, { loading: modifyingItem }] = useMutation(InventoryMutation.SAVE_INSPECTION_ITEM, {
        onCompleted: (response) => {
            if (response?.saveInspectionItem) {
                dispatch({
                    type: ACTION_TYPES.MODIFY_ITEM,
                    value: options.current,
                });

                ModalUtils.successMessage(null, 'Item modified successfully');
                // eslint-disable-next-line no-use-before-define
                if (isAddFormOpen) toggleAddForm();
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [addCategory, { loading: addingCategory }] = useMutation(InventoryMutation.ADD_INSPECTION_CATEGORY, {
        onCompleted: (response) => {
            if (response && !isCatalogManagerFormOpen) {
                const id = response.addInspectionCategory;
                const { name, order } = options.current;
                dispatch({
                    type: ACTION_TYPES.ADD_CATEGORY,
                    value: {
                        id,
                        name,
                        order,
                    },
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [editCategory] = useMutation(InventoryMutation.SAVE_INSPECTION_CATEGORY, {
        onError: (error) => {
            ModalUtils.errorMessage(null, error);
        },
    });

    const [saveInventorySettings] = useMutation(InventoryMutation.SAVE_INVENTORY_SETTINGS, {
        onCompleted: (response) => {
            if (response) {
                ModalUtils.successMessage(null, 'Max Recon Cost saved successfully');
                return;
            }

            ModalUtils.errorMessage(null, 'There was an error trying to save the Max Recon Cost');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const [saveSettings, { loading: isSavingSettings }] = useMutation(LotMutation.SAVE_LOT_DEFAULTS, {
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    useEffect(() => {
        if (aiSettingsError) {
            ModalUtils.errorMessage(aiSettingsError?.graphQLErrors);
            return;
        }

        if (!aiSettingsLoading && aiSettings) {
            const { getSettings } = aiSettings;
            if (ArrayUtils.isNotEmpty(getSettings)) {
                const aiEnabledSetting = getSettings.find((setting) => setting.key === AI_ENABLED_FOR_RECON_APPROVALS)?.value ?? 'false';
                const aiAssistantSetting = getSettings.find((setting) => setting.key === AI_ASSISTANT_FOR_RECON_APPROVALS)?.value;

                dispatch({
                    type: ACTION_TYPES.CHANGE_AI_DATA,
                    value: {
                        aiEnabled: aiEnabledSetting === 'true',
                        aiAssistant: aiAssistantSetting,
                    },
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiSettingsLoading, aiSettingsError]);

    useEffect(() => {
        if (errorLoadingAssistants) {
            ModalUtils.errorMessage(errorLoadingAssistants?.graphQLErrors);
            return;
        }

        if (!loadingSssistants && assistantsData) {
            dispatch({
                type: ACTION_TYPES.SET_ASSISTANTS,
                value: assistantsData?.pullAsistants,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingSssistants, errorLoadingAssistants]);

    useEffect(() => {
        if (usersDataError) {
            ModalUtils.errorMessage(usersDataError?.graphQLErrors);
            return;
        }

        if (!usersDataLoading && usersData) {
            const data = usersData.getUsersByCompany;
            dispatch({
                type: ACTION_TYPES.SET_USERS,
                value: data,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersDataLoading, usersDataError]);

    useEffect(() => {
        if (settingsError) {
            ModalUtils.errorMessage(settingsError?.graphQLErrors);
            return;
        }

        if (!settingsLoading && settingsData) {
            const { getSettings } = settingsData;
            if (ArrayUtils.isNotEmpty(getSettings)) {
                const value = Number(getSettings[0].value || 0);

                dispatch({
                    type: ACTION_TYPES.SET_DEFAULT_RECON_COST,
                    value,
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settingsError]);

    useEffect(() => {
        getCategories({
            variables: {
                activeItems: currentFilter === 0,
            },
        });

        getJobTypes({
            variables: {
                descriptions: [CatalogEnum.JOB_TYPE],
                onlyActive: true,
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFilter]);

    useEffect(() => {
        if (reconInspectorsError) {
            ModalUtils.errorMessage(reconInspectorsError?.graphQLErrors);
            return;
        }

        if (!reconInspectorsLoading) {
            const { getReconInspectors } = reconInspectorsData;
            dispatch({
                type: ACTION_TYPES.SET_INSPECTORS,
                value: getReconInspectors,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reconInspectorsLoading, reconInspectorsError]);

    const onSaveReconCost = () => {
        if (defaultReconCost < 0) return;

        saveInventorySettings({
            variables: {
                input: [{
                    value: defaultReconCost.toString(),
                    key: InventoryCategory.INV_DEFAULT_MAX_RECON_COST,
                    lotName: ALL_LOTS,
                }],
            },
        });
    };

    const changeFilter = (value) => {
        dispatch({
            type: ACTION_TYPES.SET_CURRENT_FILTER,
            value,
        });
    };

    const toggleAddForm = () => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_ADD_FORM,
        });
    };

    const toggleCatalogManager = (isJobTypes) => {
        refetchCategories();
        refetchJobTypes();

        dispatch({
            type: ACTION_TYPES.TOGGLE_CATALOG_MANAGER_FORM,
            value: isJobTypes,
        });
    };

    const onSaveJobTypes = (data) => {
        try {
            editJobTypes({
                variables: {
                    enumValues: data.map((el) => ({
                        ...el,
                        enumValuesId: el.enumValuesId === -1 ? null : el.enumValuesId,
                    })),
                },
            });
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        }
    };

    const onSaveCategories = async (data) => {
        const currentCats = data.filter((cat) => cat.reconInspectionCategoryId >= 0);
        const newCats = data.filter((cat) => cat.reconInspectionCategoryId === -1);
        const promises = [];

        try {
            newCats.forEach((cat) => {
                promises.push(
                    addCategory({
                        variables: {
                            input: {
                                name: cat.name,
                                order: cat.order,
                                jobSpotsSplit: cat.jobSpotsSplit,
                                forTechnicians: cat.forTechnicians,
                            },
                        },
                    }),
                );
            });

            currentCats.forEach((cat) => {
                promises.push(
                    editCategory({
                        variables: {
                            input: {
                                reconInspectionCategoryId: cat.reconInspectionCategoryId,
                                name: cat.name,
                                order: cat.order,
                                active: cat.active,
                                jobSpotsSplit: cat.jobSpotsSplit,
                                forTechnicians: cat.forTechnicians,
                            },
                        },
                    }),
                );
            });

            await Promise.all(promises);
            ModalUtils.successMessage(null, 'Categories updated successfully');

            toggleCatalogManager(false);
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        }
    };

    const onChange = ({ target: { name, value } }) => {
        if (name === 'defaultReconCost') {
            dispatch({
                type: ACTION_TYPES.SET_DEFAULT_RECON_COST,
                value,
            });

            return;
        }

        if (name === 'inspector') {
            options.current = value;
            saveDefaultInspector({
                variables: {
                    userId: value,
                },
            });
            return;
        }

        if (['aiEnabled', 'aiAssistant'].includes(name)) {
            dispatch({
                type: ACTION_TYPES.CHANGE_AI_DATA,
                value: {
                    name,
                    value,
                },
            });

            return;
        }

        dispatch({
            type: ACTION_TYPES.CHANGE_FIELD_IN_SELECTED,
            value: {
                name,
                value,
            },
        });
    };

    const onCreate = (value) => {
        const payload = {
            name: value,
            order: categories.length + 1,
        };

        options.current = payload;
        addCategory({
            variables: {
                input: payload,
            },
        });
    };

    const restoreItem = (record) => {
        dispatch({
            type: ACTION_TYPES.SET_SELECTED_ITEM,
            value: record,
        });

        modifyItem({
            variables: {
                input: {
                    reconInspectionItemId: record.reconInspectionItemId,
                    reconInspectionCategoryId: record.category.reconInspectionCategoryId,
                    jobTypes: record.jobTypes || [],
                    repairPriority: record.repairPriority,
                    dontCreateServiceJob: record.dontCreateServiceJob,
                    diagnoseByTechFirst: record.diagnoseByTechFirst,
                    employeeType: record.employeeType,
                    name: record.name,
                    active: true,
                },
            },
        });
    };

    const editItem = (record) => {
        options.current = { ...record };
        dispatch({
            type: ACTION_TYPES.TOGGLE_ADD_FORM,
            value: record,
        });
    };

    const displayRemoveWarning = (record) => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_DISPLAY_WARNING,
            value: record,
        });
    };

    const onConfirmDialogClose = () => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_DISPLAY_WARNING,
            value: {},
        });
    };

    const saveItem = () => {
        try {
            if (!selectedItem.reconInspectionItemId) {
                addItem({
                    variables: {
                        input: {
                            reconInspectionCategoryId: selectedItem.category.reconInspectionCategoryId,
                            name: selectedItem.name,
                            jobTypes: selectedItem.jobTypes || [],
                            repairPriority: selectedItem.repairPriority,
                            dontCreateServiceJob: selectedItem.dontCreateServiceJob,
                            diagnoseByTechFirst: selectedItem.diagnoseByTechFirst,
                            employeeType: selectedItem.employeeType,
                        },
                    },
                });

                return;
            }

            modifyItem({
                variables: {
                    input: {
                        reconInspectionItemId: selectedItem.reconInspectionItemId,
                        reconInspectionCategoryId: selectedItem.category.reconInspectionCategoryId,
                        name: selectedItem.name,
                        jobTypes: selectedItem.jobTypes || [],
                        repairPriority: selectedItem.repairPriority,
                        dontCreateServiceJob: selectedItem.dontCreateServiceJob,
                        diagnoseByTechFirst: selectedItem.diagnoseByTechFirst,
                        employeeType: selectedItem.employeeType,
                        active: selectedItem.active,
                    },
                },
            });
        } catch (error) {
            ModalUtils.errorMessage(null, error);
        }
    };

    const isItemSaveButtonDisabled = StringUtils.isEmpty(selectedItem?.name)
                                || StringUtils.isEmpty(selectedItem?.category?.reconInspectionCategoryId)
                                || addingItem
                                || modifyingItem;
    const handleKeyDown = (e) => {
        if (e.keyCode === 13 && !isItemSaveButtonDisabled) saveItem();
    };

    const removeItem = () => {
        modifyItem({
            variables: {
                input: {
                    reconInspectionItemId: selectedItem.reconInspectionItemId,
                    reconInspectionCategoryId: selectedItem.category.reconInspectionCategoryId,
                    jobTypes: selectedItem.jobTypes || [],
                    repairPriority: selectedItem.repairPriority,
                    dontCreateServiceJob: selectedItem.dontCreateServiceJob,
                    diagnoseByTechFirst: selectedItem.diagnoseByTechFirst,
                    employeeType: selectedItem.employeeType,
                    name: selectedItem.name,
                    active: false,
                },
            },
        });
    };

    const saveAISettings = async () => {
        saveSettings({
            variables: {
                category: LotsCategory.AI,
                key: AI_ENABLED_FOR_RECON_APPROVALS,
                value: aiEnabled ? 'true' : 'false',
                lotName: ALL_LOTS,
                critical: false,
            },
        });

        saveSettings({
            variables: {
                category: LotsCategory.AI,
                key: AI_ASSISTANT_FOR_RECON_APPROVALS,
                value: aiEnabled ? aiAssistant : '',
                lotName: ALL_LOTS,
                critical: false,
            },
        });
    };

    const getColumns = () => {
        const columns = [
            {
                headerClassName: classes.tableHeader,
                label: 'Category',
                dataKey: 'category',
                width: 250,
                cellRenderer: (cell) => {
                    const { rowData: record } = cell;

                    return (
                        <span className={classes.text}>{record.category.name}</span>
                    );
                },
            },
            {
                headerClassName: classes.tableHeader,
                label: 'Checklist Item',
                dataKey: 'name',
                width: 250,
                cellRenderer: (cell) => {
                    const { rowData: record } = cell;

                    return (
                        <span className={classes.text}>{record.name}</span>
                    );
                },
            },
            {
                headerClassName: classes.tableHeader,
                label: 'Priority',
                dataKey: 'repairPriority',
                width: 70,
                cellRenderer: (cell) => {
                    const { rowData: record } = cell;

                    return (
                        <span className={classes.text}>{record.repairPriority}</span>
                    );
                },
            },
            {
                headerClassName: classes.tableHeader,
                label: 'Actions',
                dataKey: 'actions',
                width: 200,
                cellRenderer: (cell) => {
                    const { rowData: record } = cell;
                    if (!canWrite) return null;

                    if (!record.active) {
                        return (
                            <div className={classes.actionsContainer}>
                                <Button
                                    className={classes.containedInfo}
                                    size="small"
                                    startIcon={<RestoreFromTrashIcon />}
                                    onClick={() => restoreItem(record)}
                                />
                            </div>
                        );
                    }

                    return (
                        <div className={classes.actionsContainer}>
                            <Button
                                className={classes.containedInfo}
                                size="small"
                                startIcon={<EditOutlinedIcon />}
                                onClick={() => editItem(record)}
                            />
                            <Button
                                className={classes.containedError}
                                size="small"
                                startIcon={<DeleteOutlineOutlinedIcon />}
                                onClick={() => displayRemoveWarning(record)}
                            />
                        </div>
                    );
                },
            },
        ];

        if (!isMobile) {
            columns.splice(3, 0, {
                headerClassName: classes.tableHeader,
                label: 'Job Types',
                dataKey: 'jobTypes',
                width: 350,
                cellRenderer: (cell) => {
                    const { rowData: record } = cell;
                    const current = record.jobTypes;

                    return (
                        <span className={classes.text}>{jobTypes.filter((e) => current?.includes(e.enumValuesId)).map((e) => e.description).join(', ')}</span>
                    );
                },
            });
        }

        return columns;
    };

    const categoryOptions = categories
        .map((item) => ({
            value: item.reconInspectionCategoryId,
            label: item.name,
        }));
    const defaultInspector = inspectors.find((inspector) => inspector.default);
    const inspectorOptions = inspectors
        .map((item) => ({
            value: item.userID,
            label: `${item.firstName} ${item.lastName}`,
        }));
    const items = getItems(categories);
    return (
        <>
            <Grid container className={classes.box}>
                <Grid item lg={3} xs={12}>
                    <Form.Group className={classes.fieldContainer} as={Col}>
                        <Form.Label className={classes.subLabel}>Default Person for Approvals</Form.Label>
                        <SelectWidget
                            nowrap
                            size="sm"
                            loading={reconInspectorsLoading || savingDefaultInspector}
                            className={classes.input}
                            name="inspector"
                            onChange={(name, value) => onChange({ target: { name, value } })}
                            value={defaultInspector?.userID || ''}
                            options={inspectorOptions}
                        />
                    </Form.Group>
                </Grid>
                <Grid item lg={4} xs={12}>
                    <Form.Group className={classes.fieldContainer} as={Col}>
                        <Form.Label className={classes.subLabel}>Max Recon Cost</Form.Label>
                        <div className="max-cost">
                            <InputNumber
                                value={defaultReconCost}
                                onChange={(newValue) => onChange({ target: { name: 'defaultReconCost', value: newValue } })}
                                placeholder="$0"
                                showCurrency
                                min={0}
                                decimalScale={0}
                                thousandSeparator
                                size="sm"
                            />
                            <Button
                                size="small"
                                className={classes.containedSecondaryInfo}
                                onClick={onSaveReconCost}
                            >
                                Save
                            </Button>
                        </div>
                    </Form.Group>
                </Grid>
                <Grid item xs={12}>
                    <Form.Group as={Col}>
                        <Form.Label className={classes.labels}>Inspection Items</Form.Label>
                        <Divider />
                    </Form.Group>
                </Grid>
                <Grid item lg={11} xs={12}>
                    <FormControl component="fieldset" className={clsx(classes.boxContainer, classes.paddingLeft25)}>
                        <RadioGroup row value={currentFilter} onChange={(e, val) => changeFilter(Number(val))}>
                            <FormControlLabel
                                value={0}
                                control={<Radio color="primary" />}
                                label="Active"
                            />
                            <FormControlLabel
                                value={1}
                                control={<Radio color="primary" />}
                                label="Inactive"
                            />
                        </RadioGroup>
                        <div className={classes.otherActions}>
                            <If condition={canWrite}>
                                <Button
                                    size="small"
                                    startIcon={<AddOutlinedIcon />}
                                    className={classes.containedSecondaryInfo}
                                    onClick={toggleAddForm}
                                >
                                    Add Item
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<ImportExportOutlinedIcon />}
                                    className={classes.containedSecondaryInfo}
                                    onClick={() => toggleCatalogManager(false)}
                                >
                                    {isMobile ? 'Categories' : 'Manage Categories'}
                                </Button>
                                <Button
                                    size="small"
                                    className={classes.containedSecondaryInfo}
                                    onClick={() => toggleCatalogManager(true)}
                                >
                                    {isMobile ? 'Job Types' : 'Manage Job Types'}
                                </Button>
                            </If>
                        </div>
                    </FormControl>
                </Grid>
                <Grid item lg={11} xs={12}>
                    <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        <VirtualTable
                            loading={loadingCategories}
                            rowHeight={45}
                            totalRecords={items.length}
                            data={items}
                            columns={getColumns()}
                        />
                    </div>
                </Grid>
            </Grid>
            <Grid className={classes.aiSection} lg={11} xs={12}>
                <div>AI Assistant for Recon Approvals</div>
                <div>
                    <Checkbox
                        color="primary"
                        value="selected"
                        name="aiEnabled"
                        style={{ padding: 0 }}
                        checked={aiEnabled}
                        onChange={(e) => onChange({ target: { name: e.target.name, value: e.target.checked } })}
                        inputProps={{
                            'aria-label': 'primary checkbox',
                        }}
                    />
                    <SelectWidget
                        nowrap
                        placeholder="Assistant"
                        className={clsx(classes.input, aiEnabled && !aiAssistant ? 'invalid-field' : '')}
                        name="aiAssistant"
                        value={aiAssistant}
                        onChange={(name, value) => onChange({ target: { name, value } })}
                        options={assistants.map(({ aiAssistantId, name }) => ({ label: name, value: aiAssistantId }))}
                    />
                    <Button
                        disabled={(aiEnabled && !aiAssistant) || isSavingSettings}
                        size="small"
                        className={classes.containedSecondaryInfo}
                        onClick={saveAISettings}
                    >
                        Save
                    </Button>
                </div>
            </Grid>
            <Grid className={classes.assigneeParent} xs={12}>
                <div>Assignee</div>
                <div>
                    <RoleBuilder
                        canWrite={keyStore.hasPermission(Permission.RECON_SETTINGS_WRITE)}
                        category={CompanySettingCategory.INVENTORY}
                        property={CompanySettingkey.ASSIGNEE_GLOBAL_ROLE}
                    />
                </div>
            </Grid>
            <Dialog
                open={isAddFormOpen}
                maxWidth="sm"
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <DialogAppBar
                    appBarClassName={classes.AppBar}
                    title="Add/Edit Item"
                    onClose={toggleAddForm}
                    toolbarSize="md"
                />
                <DialogContent>
                    <div className={classes.content}>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Category (You can type in brand new categories)</Form.Label>
                            <SelectWidget
                                nowrap
                                creatable
                                loading={addingCategory}
                                className={classes.input}
                                name="category"
                                onCreate={(value) => onCreate(value)}
                                onChange={(name, value) => onChange(
                                    { target: { name, value } },
                                )}
                                value={selectedItem?.category?.reconInspectionCategoryId || ''}
                                options={categoryOptions}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Name (255 Chars Max)</Form.Label>
                            <Form.Control
                                maxLength="255"
                                className={classes.input}
                                type="text"
                                name="name"
                                value={selectedItem?.name || ''}
                                onChange={onChange}
                                onKeyDown={handleKeyDown}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Repair Priority</Form.Label>
                            <SelectWidget
                                nowrap
                                loading={false}
                                className={classes.input}
                                name="repairPriority"
                                onChange={(name, value) => onChange({ target: { name, value } })}
                                value={selectedItem?.repairPriority}
                                options={Array.from({ length: 8 }, (_, i) => ({
                                    value: i + 1,
                                    label: i + 1,
                                }))}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Job Types</Form.Label>
                            <Select
                                className={classes.select}
                                multiple
                                name="jobTypes"
                                value={selectedItem?.jobTypes || []}
                                onChange={onChange}
                                renderValue={(selected) => selected.map((id) => jobTypes.find((el) => el.enumValuesId === id)?.description).join(', ')}
                                MenuProps={MenuProps}
                            >
                                {jobTypes.map(({ enumValuesId, description }, index) => (
                                    <MenuItem key={index} value={enumValuesId}>
                                        <Checkbox checked={(selectedItem.jobTypes || []).includes(enumValuesId)} />
                                        <ListItemText primary={description} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.dialogLabels}>Employee Type</Form.Label>
                            <SelectWidget
                                nowrap
                                loading={false}
                                className={classes.input}
                                name="employeeType"
                                onChange={(name, value) => onChange({ target: { name, value } })}
                                value={selectedItem?.employeeType}
                                options={[
                                    {
                                        value: null,
                                        label: 'None',
                                    },
                                    ...(
                                        [
                                            ...new Set(users.map((user) => user.employeeType).sort((a, b) => a.localeCompare(b))),
                                        ].map((employeeType) => ({
                                            value: employeeType,
                                            label: employeeType,
                                        }))
                                    ),
                                ]}
                            />
                        </Form.Group>
                        <div className={classes.flags}>
                            <Form.Label>Don&apos;t create RO Job</Form.Label>
                            <Checkbox
                                color="primary"
                                value="selected"
                                name="dontCreateServiceJob"
                                style={{ padding: 0 }}
                                checked={selectedItem?.dontCreateServiceJob ?? false}
                                onChange={(e) => onChange({ target: { name: e.target.name, value: e.target.checked } })}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                            />
                        </div>
                        <div className={classes.flags}>
                            <Form.Label>Diagnose by tech first</Form.Label>
                            <Checkbox
                                color="primary"
                                value="selected"
                                name="diagnoseByTechFirst"
                                style={{ padding: 0 }}
                                checked={selectedItem?.diagnoseByTechFirst ?? false}
                                onChange={(e) => onChange({ target: { name: e.target.name, value: e.target.checked } })}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Form.Group as={Col}>
                        <Button
                            size="small"
                            className={classes.containedSecondaryInfo}
                            disabled={isItemSaveButtonDisabled}
                            onClick={saveItem}
                        >
                            Save
                        </Button>
                    </Form.Group>
                </DialogActions>
            </Dialog>
            <If condition={isCatalogManagerFormOpen && !loadingCategories && !loadingJobTypes}>
                <CatalogManager
                    isJobTypesCatalog={isJobTypesCatalog}
                    categories={categories}
                    jobTypes={jobTypes}
                    onClose={() => toggleCatalogManager(false)}
                    onSave={isJobTypesCatalog ? onSaveJobTypes : onSaveCategories}
                />
            </If>
            <ConfirmDialog
                title="Attention!"
                description={`
                    This item will be deactivated, however we will keep current associations.
                    Later you can reactivate it on the inactivate items.
                    Continue?
                `}
                open={isRemoveWarningOpen}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={onConfirmDialogClose}
                onClickSecondary={onConfirmDialogClose}
                onClickPrimary={removeItem}
            />
        </>
    );
};

ReconSettings.propTypes = {
    canWrite: PropTypes.bool.isRequired,
};

export default ReconSettings;
